import React from 'react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, TreePine, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: '#sakums', label: t('footer.quickLinks.home', 'Sākums') },
    { href: '#pakalpojumi', label: t('footer.quickLinks.services', 'Pakalpojumi') },
    { href: '#process', label: t('footer.quickLinks.process', 'Kā notiek sadarbība') },
    { href: '#portals', label: t('footer.quickLinks.portal', 'Klientu portāls') },
    { href: '#kapec', label: t('footer.quickLinks.why', 'Kāpēc neatkarīga vērtēšana') },
    { href: '#par-mums', label: t('footer.quickLinks.about', 'Par mums') },
    { href: '#kontakti', label: t('footer.quickLinks.contact', 'Kontakti') },
  ];

  const services = [
    t('footer.services.assessment', 'Cirsmas vērtēšana'),
    t('footer.services.measurements', 'Meža uzmērījumi'),
    t('footer.services.documentation', 'Dokumentu sagatavošana'),
    t('footer.services.euProjects', 'ES projekti'),
  ];

  return (
    <footer className="section-footer text-white relative overflow-hidden">
      <div className="absolute inset-0 forest-pattern opacity-20" />
      <div className="absolute inset-0 forest-grain opacity-30" />
      {/* Decorative top wave */}
      <div className="absolute top-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full rotate-180">
          <path d="M0 60V30C240 50 480 10 720 30C960 50 1200 10 1440 30V60H0Z" fill="#f8faf8"/>
        </svg>
      </div>

      {/* Background decorations */}
      <div className="absolute top-20 right-10 opacity-5">
        <TreePine className="w-48 h-48" />
      </div>
      <div className="absolute bottom-10 left-10 opacity-5">
        <Leaf className="w-32 h-32" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <a href="#sakums" className="flex items-center gap-4 mb-6 group">
              <img
                src={companyInfo.logo.transparent}
                alt={companyInfo.name}
                className="h-16 w-auto group-hover:scale-105 transition-transform duration-300"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold">{companyInfo.name}</span>
                <span className="text-sm text-white/60">{t('company.slogan', companyInfo.slogan)}</span>
              </div>
            </a>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {t('footer.companyDescription', 'Neatkarīga cirsmu vērtēšana un mežsaimniecības pakalpojumi visā Latvijā. Strādājam tikai meža īpašnieka interesēs.')}
            </p>
            <p className="text-white/80 text-sm font-medium flex items-center gap-2">
              <Leaf className="w-4 h-4 text-green-400" />
              {t('footer.companyNote', 'Sadarbojamies gan ar fiziskām, gan ar juridiskām personām.')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-6 flex items-center gap-2">
              <div className="w-1 h-6 bg-green-400 rounded" />
              {t('footer.navigationTitle', 'Navigācija')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white hover:translate-x-1 inline-block transition-all duration-300 text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-6 flex items-center gap-2">
              <div className="w-1 h-6 bg-green-400 rounded" />
              {t('footer.servicesTitle', 'Pakalpojumi')}
            </h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <a
                    href="#pakalpojumi"
                    className="text-white/60 hover:text-white hover:translate-x-1 inline-block transition-all duration-300 text-sm"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-6 flex items-center gap-2">
              <div className="w-1 h-6 bg-green-400 rounded" />
              {t('footer.contactTitle', 'Kontakti')}
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-all duration-300 text-sm group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-green-400/20 transition-colors duration-300">
                    <Phone className="w-4 h-4" />
                  </div>
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-all duration-300 text-sm group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-green-400/20 transition-colors duration-300">
                    <Mail className="w-4 h-4" />
                  </div>
                  {companyInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <span>{companyInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} {companyInfo.legalName}. {t('footer.rights', 'Visas tiesības aizsargātas.')}
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privatuma-politika"
                className="text-white/40 hover:text-white/60 transition-colors text-sm"
                data-testid="footer-privacy-link"
              >
                {t('footer.privacy', 'Privātuma politika')}
              </Link>
              <a
                href="#"
                className="text-white/40 hover:text-white/60 transition-colors text-sm"
              >
                {t('footer.terms', 'Lietošanas noteikumi')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
